import {
  createContext, ReactElement, ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import { filterUnitByOptions } from '../helpers/filters';
import { BaseOptions, FilterOptions, ViewOption } from '../types';

export type SelectedOptions = {
  state: string[] | undefined,
  status: string | undefined,
  group: { value: string, units: number[] } | undefined,
  healthStatus: string | undefined,
  cities: { value: string, stateId: number }[] | undefined,
  units: { value: string, clientId: number, stateId: number, cityId: string }[] | undefined
}

export interface FilterContextType {
  clients: string[];
  baseOptions: BaseOptions;
  filterOptions: FilterOptions;
  viewOption: ViewOption;
  selectedOptions: SelectedOptions;
  filtersDependencies: unknown[];
  clearFilter: (key: keyof SelectedOptions) => void;
  onSelectOption: (key: keyof SelectedOptions, value: string, additionalValue?: number[]) => void;
  onSelectView: (view: ViewOption) => void;
  onSelectClient: (value: string) => void;
  setBaseOptions: (baseOptions: BaseOptions) => void;
  setFilterOptions: (filterOptions: FilterOptions) => void;
  clearAllFilters: () => void;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const FilterProvider = ({
  children,
}: Readonly<{ children: ReactNode }>): ReactElement => {
  const [clients, setClients] = useState<string[]>([]);
  const [viewOption, setViewOption] = useState<ViewOption>('health');
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({
    group: undefined,
    state: undefined,
    status: undefined,
    healthStatus: undefined,
    cities: undefined,
    units: undefined,
  });
  const [baseOptions, setBaseOptions] = useState<BaseOptions>({
    cities: [],
    units: [],
    states: [],
  });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    states: [],
    groups: [],
    clients: [],
    cities: [],
    units: [],
  });

  const hasOptionSelected = useCallback((key: keyof SelectedOptions, value: string): boolean => {
    if (key === 'state') return !!selectedOptions.state?.includes(value);
    if (key === 'cities') return !!selectedOptions.cities?.some((item) => item.value === value);
    if (key === 'units') return !!selectedOptions.units?.some((item) => item.value === value);
    if (key === 'group') return selectedOptions[key]?.value === value;

    return selectedOptions[key] === value;
  }, [selectedOptions]);

  const filtersDependencies = useMemo(() => [
    clients,
    selectedOptions.group?.units,
    selectedOptions.state,
    selectedOptions.healthStatus,
    selectedOptions.status,
    selectedOptions.cities,
    selectedOptions.units,
  ], [
    clients,
    selectedOptions.group?.units,
    selectedOptions.healthStatus,
    selectedOptions.state,
    selectedOptions.status,
    selectedOptions.cities,
    selectedOptions.units,
  ]);

  const clearAllFilters = useCallback((): void => {
    setSelectedOptions({
      cities: undefined,
      group: undefined,
      healthStatus: undefined,
      state: undefined,
      status: undefined,
      units: undefined,
    });

    setFilterOptions((previousValues) => ({
      ...previousValues,
      cities: baseOptions.cities,
      units: baseOptions.units,
    }));
  }, [baseOptions.cities, baseOptions.units]);

  const onSelectState = useCallback((prevValues: SelectedOptions, newValues: SelectedOptions, value: string): SelectedOptions => {
    const updatedValues = { ...newValues };
    const options = prevValues.state ?? [];
    if (options.includes(value)) {
      updatedValues.state = options.filter((item) => item !== value);
    } else {
      updatedValues.state = [...options, value];
    }

    if (selectedOptions.cities && selectedOptions.cities.length > 0) {
      updatedValues.cities = [];
    }

    if (selectedOptions.units && selectedOptions.units.length > 0) {
      updatedValues.units = [];
    }

    setFilterOptions((previousOptions) => {
      const hasSelectedState = updatedValues.state && updatedValues.state.length > 0;

      const newCities = hasSelectedState
        ? baseOptions.cities.filter((item) => updatedValues.state?.includes(String(item.stateId)))
        : baseOptions.cities;

      const newUnits = baseOptions.units.filter((unit) => filterUnitByOptions(updatedValues, unit, clients));

      const newFilterOptions = {
        ...previousOptions,
        cities: newCities,
        units: newUnits,
      };

      return newFilterOptions;
    });

    return updatedValues;
  }, [baseOptions.cities, baseOptions.units, clients, selectedOptions.cities, selectedOptions.units]);

  const onSelectCity = useCallback((prevValues: SelectedOptions, newValues: SelectedOptions, value: string): SelectedOptions => {
    const updatedValues = { ...newValues };
    const options = prevValues.cities ?? [];
    if (hasOptionSelected('cities', value)) {
      updatedValues.cities = options.filter((item) => item.value !== value);
    } else {
      const option = filterOptions.cities.find((item) => item.value === value);
      if (!option) return updatedValues;

      updatedValues.cities = [...options, {
        stateId: option.stateId,
        value: option.value,
      }];
    }

    if (selectedOptions.units && selectedOptions.units.length > 0) {
      updatedValues.units = [];
    }

    setFilterOptions((previousOptions) => {
      const newUnits = baseOptions.units.filter((unit) => filterUnitByOptions(updatedValues, unit, clients));

      const newFilterOptions = {
        ...previousOptions,
        units: newUnits,
      };

      return newFilterOptions;
    });

    return updatedValues;
  }, [baseOptions.units, clients, filterOptions.cities, hasOptionSelected, selectedOptions.units]);

  const onSelectUnit = useCallback((prevValues: SelectedOptions, newValues: SelectedOptions, value: string): SelectedOptions => {
    const updatedValues = { ...newValues };
    const options = prevValues.units ?? [];
    if (hasOptionSelected('units', value)) {
      updatedValues.units = options.filter((item) => item.value !== value);
    } else {
      const option = filterOptions.units.find((item) => item.value === value);
      if (!option) return updatedValues;

      updatedValues.units = [...options, {
        cityId: option.cityId,
        clientId: option.clientId,
        stateId: option.stateId,
        value: option.value,
      }];
    }

    return updatedValues;
  }, [filterOptions.units, hasOptionSelected]);

  const onSelectGroup = useCallback((newValues: SelectedOptions, value: string, additionalValue?: number[]): SelectedOptions => {
    const updatedValues = { ...newValues };
    if (hasOptionSelected('group', value)) {
      updatedValues.group = undefined;
    } else {
      updatedValues.group = {
        value,
        units: additionalValue ?? [],
      };
    }

    if (selectedOptions.units && selectedOptions.units.length > 0) {
      updatedValues.units = [];
    }

    setFilterOptions((previousOptions) => {
      const newUnits = baseOptions.units.filter((unit) => filterUnitByOptions(updatedValues, unit, clients));

      const newFilterOptions = {
        ...previousOptions,
        units: newUnits,
      };

      return newFilterOptions;
    });

    return updatedValues;
  }, [baseOptions.units, clients, hasOptionSelected, selectedOptions.units]);

  const onSelectDefaultOption = useCallback((newValues: SelectedOptions, key: keyof SelectedOptions, value: string): SelectedOptions => {
    if (hasOptionSelected(key, value)) {
      newValues[key] = undefined;
    } else {
      newValues[key as 'status' | 'healthStatus'] = value;
    }

    return newValues;
  }, [hasOptionSelected]);

  const onSelectOption = useCallback((key: keyof SelectedOptions, value: string, additionalValue?: number[]):void => {
    setSelectedOptions((prevValues) => {
      const newValues = { ...prevValues };

      switch (key) {
        case 'group': {
          return onSelectGroup(newValues, value, additionalValue);
        }
        case 'cities': {
          return onSelectCity(prevValues, newValues, value);
        }
        case 'state': {
          return onSelectState(prevValues, newValues, value);
        }
        case 'units': {
          return onSelectUnit(prevValues, newValues, value);
        }
        default: return onSelectDefaultOption(newValues, key, value);
      }
    });
  }, [onSelectDefaultOption, onSelectGroup, onSelectCity, onSelectState, onSelectUnit]);

  function onSelectView(view: ViewOption): void {
    setViewOption(view);
    if (view === 'health') {
      clearFilter('status');
    } else {
      clearFilter('healthStatus');
    }
  }

  const clearFilter = useCallback((key: keyof SelectedOptions): void => {
    setSelectedOptions((prevOptions) => ({
      ...prevOptions,
      [key]: undefined,
    }));

    if (key === 'cities') {
      setFilterOptions((previousValues) => ({
        ...previousValues,
        cities: baseOptions.cities,
      }));
    }

    if (key === 'units') {
      setFilterOptions((previousValues) => ({
        ...previousValues,
        units: baseOptions.units,
      }));
    }
  }, [baseOptions]);

  const onSelectClient = useCallback((value: string): void => {
    const hasClientSelected = clients?.includes(value);

    const newClients = hasClientSelected
      ? clients.filter((item) => item !== value)
      : [...clients, value];

    setClients(newClients);

    clearAllFilters();

    const clientOptions = filterOptions.clients.filter((item) => newClients.includes(item.value));
    console.log('clientOptions', clientOptions);

    setFilterOptions((prevValues) => ({
      ...prevValues,
      cities: baseOptions.cities.filter((city) => clientOptions.some((item) => item.cities.includes(city.value))),
      states: baseOptions.states.filter((state) => clientOptions.some((item) => item.states.includes(Number(state.value)))),
      units: baseOptions.units.filter((unit) => filterUnitByOptions(selectedOptions, unit, newClients)),
    }));
  }, [
    baseOptions.cities,
    baseOptions.states,
    baseOptions.units,
    clearAllFilters,
    clients,
    filterOptions.clients,
    selectedOptions,
  ]);

  const value = useMemo((): FilterContextType => ({
    baseOptions,
    clients,
    clearFilter,
    onSelectClient,
    onSelectOption,
    onSelectView,
    selectedOptions,
    viewOption,
    filtersDependencies,
    filterOptions,
    setBaseOptions,
    setFilterOptions,
    clearAllFilters,
  }), [
    baseOptions,
    clients,
    onSelectClient,
    onSelectOption,
    selectedOptions,
    viewOption,
    filtersDependencies,
    filterOptions,
    clearAllFilters,
  ]);

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

export const useFilterContext = (): FilterContextType => {
  const context = useContext(FilterContext);

  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider.');
  }

  return context;
};
